import { useState, useEffect, useMemo } from "react";
import { Accordion, Button, FormRow } from 'components/common';
import { Controller, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import { CRM_CO_TYPE_OF_CO } from "app_config";
import { makeEmployeeLoader } from "helpers/debounce";
import EmployeeSelectField from "components/common/select/EmployeeSelectField";
import AddChangeForm from "./AddChangeForm";
import { MdDelete, MdEdit } from "react-icons/md";
import { FaBan, FaPlus, FaSave } from "react-icons/fa";
import { createChangeOrder, updateChangeOrder } from "redux/crm/project/changeOrderSlice";
import React from 'react';
import PropTypes from 'prop-types';
import { INPUT_CLS, TEXTAREA_CLS } from "helpers/formStyles";

const BODY_CLS = "tw-p-4";
const CHECKBOX_CLS = "tw-w-4 tw-h-4 tw-accent-blue-600 tw-cursor-pointer";
const CHECKBOX_LABEL_CLS = "tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none tw-mb-0 tw-text-sm tw-text-slate-700";
import { TH_CLS, TD_CLS } from 'helpers/formStyles';

// Impact-of-changes rows are read-only placeholders (no form binding) — the
// values are computed elsewhere. Kept disabled exactly as in the original form.
const IMPACT_ROWS = [
  'in STD Ex-works (US$)',
  'in CFR (US$)',
  'in S.B.O. (US$)',
  'in Delivery Period (Weeks)',
  'in Freight (US$)',
  'in Erection (US$)',
  'in Total Contract (US$)',
  'in Erection Period (Weeks)',
];

const CONTRACT_UPDATE_HEADERS = [
  'C.O#', 'Date', 'Std. Ex-Works', 'Freight', 'CFR/CIF Job Site',
  'SBO', 'Erection', 'Others', 'Total Contract',
];

// ── Controller-bound checkbox ────────────────────────────────────────────────
// field.onChange(e) is passed the raw event on purpose: react-hook-form reads
// `checked` off checkbox targets, matching the previous Form.Check behaviour.
const ControlledCheckbox = ({ control, name, label }) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <label className={CHECKBOX_LABEL_CLS}>
        <input
          type="checkbox"
          className={CHECKBOX_CLS}
          checked={!!field.value}
          onChange={(e) => field.onChange(e)}
        />
        <span>{label}</span>
      </label>
    )}
  />
);
ControlledCheckbox.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

// ── Static (disabled) checkbox ───────────────────────────────────────────────
const DisabledCheckbox = ({ label }) => (
  <label className="tw-flex tw-items-center tw-gap-2 tw-select-none tw-mb-0 tw-text-sm tw-text-slate-500 tw-cursor-not-allowed">
    <input type="checkbox" disabled className="tw-w-4 tw-h-4 tw-cursor-not-allowed" />
    <span>{label}</span>
  </label>
);
DisabledCheckbox.propTypes = { label: PropTypes.string.isRequired };

// ── Date field bound to react-hook-form ──────────────────────────────────────
const DateField = ({ control, name, rules }) => (
  <Controller
    control={control}
    name={name}
    rules={rules}
    render={({ field }) => (
      <DatePicker
        selected={field.value}
        onChange={d => field.onChange(adjustToNoon(d))}
        className={INPUT_CLS}
        placeholderText="dd-MMM-YYYY"
        dateFormat="dd-MMM-yyyy"
        fixedHeight
      />
    )}
  />
);
DateField.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  rules: PropTypes.object,
};

const ChangeOrderForm = ({project, building, changeOrder, title, onSave, embedded = false, onCancel}) => {
  const {control, register, handleSubmit, formState: {errors}, setValue} = useForm()
  const [activeKey, setActiveKey] = useState(Array.from({ length: 10 }, (_, index) => index.toString()).filter(key => key !== "5"))
  const [showChangeForm, setShowChangeForm] = useState(false)
  const [descriptionChanges, setDescriptionChanges] = useState([])
  const [descriptionChange, setDescriptionChange] = useState(null)
  //const [files, setFiles] = useState([])

  const dispatch = useDispatch()
  const navigate = useNavigate()
  useEffect(() => {
    setValue("co", building?.next_change_order || '01')
    setValue("entry_date", adjustToNoon(new Date()))
    setValue("area_date", adjustToNoon(new Date()))
    setValue("head_date", adjustToNoon(new Date()))

    setValue("cse_head", building.cse_order_entry[0]);

    if (changeOrder) {
      Object.entries(changeOrder.change_order_form).forEach(([key, value]) => {
        switch(key) {
          case "entry_date":
          case "area_date":
          case "head_date":
            setValue(key, new Date(value))
            break
          case "description_of_changes":
            setDescriptionChanges(value)
            break
          default:
            setValue(key, value)
            break
        }
      })
    }
    // Seed only when project/building change; re-seeding on every changeOrder
    // refresh would wipe the user's in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, building, setValue])

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  // Each select gets its own debounce instance to prevent shared timer cancellation
  const cseAreaOptions = useMemo(() => makeEmployeeLoader(), [])
  const csmAreaOptions = useMemo(() => makeEmployeeLoader(), [])
  const cseHeadOptions = useMemo(() => makeEmployeeLoader(), [])
  const csmHeadOptions = useMemo(() => makeEmployeeLoader(), [])

  const defaultCSEHeadOption = useMemo(() => {
    const name = changeOrder?.change_order_form.cse_head || building?.cse_order_entry[0]
    return name ? { value: name, label: name } : null
  }, [building, changeOrder])

  const defaultCSMHeadOption = useMemo(() => {
    const name = changeOrder?.change_order_form.csm_head
    return name ? { value: name, label: name } : null
  }, [changeOrder])

  const defaultCSEAreaOption = useMemo(() => {
    const name = changeOrder?.change_order_form.cse_area
    return name ? { value: name, label: name } : null
  }, [changeOrder])

  const defaultCSMAreaOption = useMemo(() => {
    const name = changeOrder?.change_order_form.csm_area
    return name ? { value: name, label: name } : null
  }, [changeOrder])

  useEffect(() => {
    if (defaultCSEAreaOption) setValue('cse_area', defaultCSEAreaOption.value)
    if (defaultCSMAreaOption) setValue('csm_area', defaultCSMAreaOption.value)
    if (defaultCSEHeadOption) setValue('cse_head', defaultCSEHeadOption.value)
    if (defaultCSMHeadOption) setValue('csm_head', defaultCSMHeadOption.value)
  }, [defaultCSEAreaOption, defaultCSMAreaOption, defaultCSEHeadOption, defaultCSMHeadOption, setValue])

  const handleAddChange = () => {
    setShowChangeForm(true)
    setDescriptionChange(null)
  }

  const handleEditChange = (editedItem) => {
    setShowChangeForm(true)
    setDescriptionChange(editedItem)
  }

  const handleDeleteChange = (deletedItem) => {
    setDescriptionChanges((prevChanges) =>
      prevChanges.filter((change) => change.change_no !== deletedItem.change_no)
    )
    toast.success(`Change no#${deletedItem.change_no} removed.`)
  }

  const validateDescriptionOfChange = () => {
    if (!descriptionChanges || descriptionChanges.length === 0)
    {
      toast.error("Description of changes must be required.")
      return false
    }
    return true
  }

  const onSubmit = async (formData) => {
    if (!validateDescriptionOfChange()) {
      return
    }

    const data = {
      co: formData.co,
      entry_date: formData.entry_date,
      cse_area: formData.cse_area,
      csm_area: formData.csm_area,
      cse_head: formData.cse_head,
      csm_head: formData.csm_head,
      change_order_form: {
        ...formData,
        description_of_changes: descriptionChanges
      },
     // change_order_documents: files || null
    }

    // Caller-managed save (e.g. CO revision form tab) — skip the CO dispatch/redirect.
    if (onSave) {
      await onSave(data)
      return
    }

    try {
      const projectId = project.id
      const buildingId = building.id
      let changeOrderId = null

      if (changeOrder) {
        changeOrderId = changeOrder.id
        dispatch(updateChangeOrder({projectId, buildingId, changeOrderId, data}))
      }
      else
      {
        const response = await dispatch(createChangeOrder({projectId, buildingId, data}))
        changeOrderId = response.payload.data.id
      }

      if (changeOrderId) {
        navigate(`/crm/projects/${projectId}/buildings/${buildingId}/change-orders/${changeOrderId}`)
      }
    }
    catch (error) {
      console.error(error.message)
      toast.error(error.message)
    }
  }

  return (
    <>
      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-3 tw-mb-3">
        <h5 className="tw-text-center tw-text-base tw-font-semibold tw-uppercase tw-text-slate-800 tw-mb-0">
          {title}
        </h5>
      </div>

      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-pb-20">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Accordion
            className="tw-text-sm"
            activeKeys={activeKey}
            onChange={handleAccordionSelect}
          >
            {/* 1. General Information */}
            <Accordion.Item eventKey="0" header="1. General Information" bodyClassName={BODY_CLS}>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div>
                  <div className="tw-flex tw-mb-3">
                    <span className="tw-w-1/3 tw-text-sm tw-text-slate-600">Job No</span>
                    <span className="tw-flex-1 tw-text-sm tw-text-blue-600">{project?.j_number}</span>
                  </div>
                  <div className="tw-flex tw-mb-3">
                    <span className="tw-w-1/3 tw-text-sm tw-text-slate-600">Customer</span>
                    <span className="tw-flex-1 tw-text-sm tw-text-blue-600">
                      {project?.customer_name ? project.customer_name : project?.customer.name}
                    </span>
                  </div>
                  <div className="tw-flex">
                    <span className="tw-w-1/3 tw-text-sm tw-text-slate-600">Type of C.O</span>
                    <div className="tw-flex-1">
                      <Controller
                        control={control}
                        name="type_of_co"
                        defaultValue={CRM_CO_TYPE_OF_CO[0]}
                        render={({field}) => (
                          <div className="tw-flex tw-flex-col tw-gap-2">
                            {CRM_CO_TYPE_OF_CO.map((option, index) => (
                              <label key={option} htmlFor={`${option}${index}`} className={CHECKBOX_LABEL_CLS}>
                                <input
                                  id={`${option}${index}`}
                                  type="radio"
                                  name={field.name}
                                  value={option}
                                  className={CHECKBOX_CLS}
                                  checked={field.value === option}
                                  onChange={() => field.onChange(option)}
                                  onBlur={field.onBlur}
                                />
                                <span>{option}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <FormRow label="This Change Order No." error={errors.co?.message}>
                    <input
                      type="text"
                      placeholder="Change order number"
                      className={INPUT_CLS}
                      {...register("co", {
                        required: "Change order number must be required."
                      })}
                    />
                  </FormRow>

                  <FormRow label="Date this C.O. Entered" error={errors.entry_date?.message}>
                    <DateField
                      control={control}
                      name="entry_date"
                      rules={{required: "Date this C.O. Entered must be required"}}
                    />
                  </FormRow>

                  <FormRow label="Continuation Sheet Attached:">
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <Controller
                        control={control}
                        name="continuation_sheet_attached"
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            className={CHECKBOX_CLS}
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e)}
                          />
                        )}
                      />
                      <div className="tw-text-sm tw-text-slate-600">
                        Number of sheet: <strong>1</strong>
                      </div>
                    </div>
                  </FormRow>
                </div>
              </div>
            </Accordion.Item>

            {/* 2. Payment for this CO */}
            <Accordion.Item eventKey="1" header="2. Payment for this CO." bodyClassName={BODY_CLS}>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <FormRow label="Price of this C.O." error={errors.payment_of_price_co?.message}>
                  <input
                    type="text"
                    placeholder="Price of this C.O."
                    className={INPUT_CLS}
                    {...register("payment_of_price_co")}
                  />
                </FormRow>
                <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-4 tw-mb-4">
                  <ControlledCheckbox control={control} name="to_be_established" label="To be established" />
                  <ControlledCheckbox control={control} name="already_included_contract" label="Already included contract" />
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <FormRow label="Mode of Payment for this C.O." error={errors.payment_of_mode?.message}>
                  <input
                    type="text"
                    placeholder="Mode of Payment for this C.O"
                    className={INPUT_CLS}
                    {...register("payment_of_mode")}
                  />
                </FormRow>
                <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                  <div className="tw-flex tw-flex-wrap tw-gap-4">
                    <ControlledCheckbox control={control} name="amend_lc" label="Amend L/C" />
                    <ControlledCheckbox control={control} name="revise_contract" label="Revise contract value" />
                    <ControlledCheckbox control={control} name="payment_others" label="Other (specify)" />
                  </div>
                  <textarea
                    placeholder="Others"
                    className={TEXTAREA_CLS}
                    {...register("payment_others_specify")}
                  />
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <FormRow label="Customer will Pay" error={errors.customer_will_pay?.message}>
                  <input
                    type="text"
                    placeholder="Customer will Pay"
                    className={INPUT_CLS}
                    {...register("customer_will_pay")}
                  />
                </FormRow>
                <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-4 tw-mb-4">
                  <ControlledCheckbox control={control} name="fully" label="Fully" />
                  <ControlledCheckbox control={control} name="partially" label="Partially" />
                  <ControlledCheckbox control={control} name="no_charge" label="No charge" />
                </div>
              </div>
            </Accordion.Item>

            {/* 3. Engg. Documents Required after this C.O. */}
            <Accordion.Item
              eventKey="2"
              header="3. Engg. Documents Required after this C.O."
              bodyClassName={BODY_CLS}
            >
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <ControlledCheckbox control={control} name="desgin_calculation" label="Desgin calculation" />
                  <ControlledCheckbox control={control} name="approval_dwgs" label="Approval dwgs" />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <ControlledCheckbox control={control} name="re_approval_dwgs" label="Re approval dwgs" />
                  <ControlledCheckbox control={control} name="erection_dwgs" label="Erection dwgs" />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <ControlledCheckbox control={control} name="effected_sheets_of_approval_dwgs" label="Effected sheets of approval dwgs" />
                  <ControlledCheckbox control={control} name="effected_sheets_of_erection_dwgs" label="Effected sheets of erection dwgs" />
                  <ControlledCheckbox control={control} name="record_dwgs" label="Record dwgs" />
                </div>
              </div>
            </Accordion.Item>

            {/* 4. This C.O. affects. */}
            <Accordion.Item eventKey="3" header="4. This C.O. affects." bodyClassName={BODY_CLS}>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                <ControlledCheckbox control={control} name="co_affects_design" label="Desgin" />
                <ControlledCheckbox control={control} name="co_affects_drafting" label="Drafting" />
                <ControlledCheckbox control={control} name="co_affects_fabrication" label="Fabrication" />
              </div>
            </Accordion.Item>

            {/* 5. Sales Discount */}
            <Accordion.Item eventKey="4" header="5. Sales Discount" bodyClassName={BODY_CLS}>
              <FormRow label="Before this C.O.">
                <input
                  type="text"
                  placeholder="Before this C.O."
                  className={INPUT_CLS}
                  {...register("sales_discount_before")}
                />
              </FormRow>
              <FormRow label="After this C.O">
                <input
                  type="text"
                  placeholder="After this C.O"
                  className={INPUT_CLS}
                  {...register("sales_discount_after")}
                />
              </FormRow>
            </Accordion.Item>

            {/* 6. Impact of Changes */}
            <Accordion.Item eventKey="5" header="6. Impact of Changes" bodyClassName={BODY_CLS}>
              <div className="tw-flex tw-flex-col tw-gap-2">
                {IMPACT_ROWS.map((label) => (
                  <div key={label} className="tw-grid tw-grid-cols-3 tw-gap-3 tw-items-center">
                    <DisabledCheckbox label="Increase" />
                    <DisabledCheckbox label="Decrease" />
                    <span className="tw-text-sm tw-text-slate-600">{label}</span>
                  </div>
                ))}
              </div>
            </Accordion.Item>

            {/* 7. Contract Update (All Prices are in US$) */}
            <Accordion.Item
              eventKey="6"
              header="7. Contract Update (All Prices are in US$)"
              bodyClassName={BODY_CLS}
            >
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-border-collapse">
                  <thead>
                    <tr>
                      {CONTRACT_UPDATE_HEADERS.map((header) => (
                        <th key={header} className={TH_CLS}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>

              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-mt-3">
                <FormRow label="New Delivery Period (Weeks)">
                  <input
                    type="number"
                    placeholder="weeks"
                    className={INPUT_CLS}
                    {...register("new_delivery_period_weeks")}
                  />
                </FormRow>
                <FormRow label="New Erection Period (Weeks)">
                  <input
                    type="number"
                    placeholder="weeks"
                    className={INPUT_CLS}
                    {...register("new_erection_period_weeks")}
                  />
                </FormRow>
              </div>
            </Accordion.Item>

            {/* 8. Description of changes */}
            <Accordion.Item eventKey="7" header="8. Description of changes" bodyClassName={BODY_CLS}>
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-border-collapse">
                  <thead>
                    <tr>
                      <th className={TH_CLS} style={{ width: '5%' }}></th>
                      <th className={TH_CLS} style={{ width: '5%' }}>Bldg. No.</th>
                      <th className={TH_CLS} style={{ width: '5%' }}>Change No.</th>
                      <th className={TH_CLS}>Description of changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(descriptionChanges) && descriptionChanges.map((item) => (
                      <tr key={item.id} className="odd:tw-bg-slate-50">
                        <td className={TD_CLS}>
                          <div className="tw-flex tw-items-center tw-gap-1">
                            <Button
                              size="xs"
                              variant="secondary"
                              title="Edit"
                              onClick={() => handleEditChange(item)}
                            >
                              <MdEdit className="tw-text-blue-600" />
                            </Button>
                            <Button
                              size="xs"
                              variant="secondary"
                              title="Remove"
                              onClick={() => handleDeleteChange(item)}
                            >
                              <MdDelete className="tw-text-red-600" />
                            </Button>
                          </div>
                        </td>
                        <td className={TD_CLS}>{item.building_name}</td>
                        <td className={TD_CLS}>{item.change_no}</td>
                        <td className={TD_CLS}>
                          {/* Normalise pasted rich-text: force the form's font on all
                              descendants (TinyMCE <pre>/<code> would render monospace),
                              a consistent line-height (pasted content often carries a
                              tight inline line-height that overlaps), and reset block
                              margins so <p>/<pre> don't overflow or overlap. */}
                          <div
                            className="tw-text-slate-700 [&_*]:[font-family:inherit] [&_*]:!tw-leading-normal [&_p]:tw-m-0 [&_p]:tw-mb-1 [&_p:last-child]:tw-mb-0 [&_pre]:tw-m-0 [&_pre]:tw-whitespace-pre-wrap [&_code]:tw-bg-transparent"
                            dangerouslySetInnerHTML={{ __html: item.description }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="tw-flex tw-justify-end tw-mt-3">
                <Button size="sm" variant="secondary" leftIcon={FaPlus} onClick={handleAddChange}>
                  Add change
                </Button>
              </div>
            </Accordion.Item>

            {/* 9. Customer service engineer */}
            <Accordion.Item eventKey="8" header="9. Customer service engineer" bodyClassName={BODY_CLS}>
              {/* Area office */}
              <div className="tw-mb-4">
                <div className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-2">Area office</div>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                  <FormRow label="Customer Service Engineer">
                    <EmployeeSelectField
                      control={control}
                      name="cse_area"
                      required
                      requiredMessage="Name must be required"
                      loadOptions={cseAreaOptions}
                      defaultValue={defaultCSEAreaOption}
                      placeholder="Select employee…"
                    />
                  </FormRow>
                  <FormRow label="Customer Service Manager">
                    <EmployeeSelectField
                      control={control}
                      name="csm_area"
                      required
                      requiredMessage="Name must be required"
                      loadOptions={csmAreaOptions}
                      defaultValue={defaultCSMAreaOption}
                      placeholder="Select employee…"
                    />
                  </FormRow>
                  <FormRow label="Date" error={errors.area_date?.message}>
                    <DateField
                      control={control}
                      name="area_date"
                      rules={{required: "Date must be required"}}
                    />
                  </FormRow>
                </div>
              </div>

              {/* Head office */}
              <div>
                <div className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-2">Head office</div>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                  <FormRow label="Customer Service Engineer">
                    <EmployeeSelectField
                      control={control}
                      name="cse_head"
                      required
                      requiredMessage="Name must be required"
                      loadOptions={cseHeadOptions}
                      defaultValue={defaultCSEHeadOption}
                      placeholder="Select employee…"
                    />
                  </FormRow>
                  <FormRow label="Supervisor">
                    <EmployeeSelectField
                      control={control}
                      name="csm_head"
                      loadOptions={csmHeadOptions}
                      defaultValue={defaultCSMHeadOption}
                      placeholder="Select employee…"
                    />
                  </FormRow>
                  <FormRow label="Date" error={errors.head_date?.message}>
                    <DateField
                      control={control}
                      name="head_date"
                      rules={{required: "Date must be required"}}
                    />
                  </FormRow>
                </div>
              </div>
            </Accordion.Item>

            {/* 10. Attachment — kept disabled, see UploadDocument integration
            <Accordion.Item eventKey="9" header="10. Attachment" bodyClassName={BODY_CLS}>
              <div className="tw-text-xs tw-text-red-600 tw-mb-1">
                Only OUTLOOK, PDF, EXCEL, WORD, IMAGE or TEXT files are allowed. The file size is maximum of 500Mb.
              </div>
              <UploadDocument files={files} setFiles={setFiles} />
            </Accordion.Item>
            */}
          </Accordion>

          <div
            className={`${embedded ? 'tw-sticky tw-bottom-0' : 'tw-fixed tw-bottom-0 tw-left-0 tw-right-0'} tw-border-t tw-border-solid tw-border-slate-200 tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-z-50`}
            style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)' }}
          >
            <Button size="sm" type="submit" variant="primary" leftIcon={FaSave}>
              {changeOrder ? "Update" : "Create"}
            </Button>
            <Button size="sm" variant="danger" leftIcon={FaBan} onClick={onCancel || (() => navigate(-1))}>
              Cancel
            </Button>
          </div>
        </form>
      </div>

      {showChangeForm && (
        <AddChangeForm
          showModal={showChangeForm}
          setshowModal={setShowChangeForm}
          building={building}
          descriptionChanges={descriptionChanges}
          setDescriptionChanges={setDescriptionChanges}
          descriptionChange={descriptionChange}
        />
      )}
    </>
  )
};

ChangeOrderForm.propTypes = {
  project: PropTypes.object.isRequired,
  building: PropTypes.object.isRequired,
  changeOrder: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  // embedded: render the action bar sticky-to-container (inside a drawer) instead
  // of fixed-to-viewport; onCancel replaces the default router back-navigation.
  embedded: PropTypes.bool,
  onCancel: PropTypes.func
}

export default ChangeOrderForm;
